import React, { useEffect, useState } from "react";
import EventDetailsCard from "../components/event-details-card";
import EventDetailsModal from "../components/event-details-modal";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { FaSearch } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { setActivePage, setShowPreloader } from "../store/activePage";
import HandleGetEventList from "../hooks/handleGetEventList";
import handleExportReport from "../hooks/handleExportReport";
import TokenDetailsModal from "../components/token-details-modal";
import { AxiosPostMethod } from "../helpers/customAxios";
import HandleLogout from "../hooks/handleLogout";
import OperatorDetailsModal from "../components/operator-details-modal";
import { useNavigate } from "react-router-dom";

const CustomerUpcomingEvents = () => {
  const [searchValue, setSearchValue] = useState("");
  const [eventDetails, setEventDetails] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [tokenListDetails, setTokenListDetails] = useState(null);
  const [operatorListDetails, setOperatorListDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Upcoming Events",
        activeMenu: "upcomingEvents",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    fetchEvents();
  }, [searchValue]);

  const fetchEvents = async () => {
    setLoading(true);
    dispatch(setShowPreloader(true));
    const eventList = await HandleGetEventList(searchValue);
    dispatch(setShowPreloader(false));
    if (eventList) setEventDetails(eventList);
    setLoading(false);
  };

  const handleCloseModal = () => setSelectedEvent(null);

  const handleShowTokenList = async (e, eventId) => {
    if (e) e.stopPropagation();
    const res = await AxiosPostMethod("customer/get-token-info", {
      event_id: eventId,
    });
    if (res.status === 401) HandleLogout();
    if (res.data?.data?.length > 0) {
      setTokenListDetails(res.data?.data);
    }
  };

  const handleShowOperatorList = async (e, eventId) => {
    if (e) e.stopPropagation();
    const selectedEvent = eventDetails.filter(
      (items) => items.event_id === eventId
    )[0];
    if (selectedEvent?.operator_list?.length > 0) {
      setOperatorListDetails({
        event_id: selectedEvent.event_id,
        photos: selectedEvent.operator_list,
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div>
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-800">Upcoming Events</h1>
          <p className="text-xs text-slate-500 mt-1">
            Browse and manage all registered celebrations
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <input
              type="text"
              placeholder="Search by event name, city..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-xs border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-600 transition shadow-sm"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch size={12} className="text-slate-400" />
            </div>
          </div>
        </div>

        {/* Event Count */}
        {!loading && eventDetails.length > 0 && (
          <div className="mb-4 text-xs font-semibold text-slate-500">
            Found {eventDetails.length} event{eventDetails.length !== 1 && "s"}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse"
              >
                <div className="h-32 bg-gray-200"></div>
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Event Cards Grid */}
        {!loading && eventDetails.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {eventDetails.map((event, index) => (
              <EventDetailsCard
                key={index}
                eventDetails={event}
                onClick={(details) => setSelectedEvent(details)}
                cardStyle="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden"
                onDownloadReport={(filePath) =>
                  handleExportReport(event.event_id, dispatch)
                }
                token_count={event.token_count}
                operator_count={event.operator_count}
                handleShowTokenList={(e, eventId) =>
                  handleShowTokenList(e, eventId)
                }
                handleShowOperatorList={(e, eventId) =>
                  handleShowOperatorList(e, eventId)
                }
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && eventDetails.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              No upcoming events found
            </h3>
            <p className="text-gray-500">
              {searchValue
                ? "Try adjusting your search or check back later."
                : "New events will appear here once scheduled."}
            </p>
          </div>
        )}

        {/* Modals */}
        {selectedEvent && (
          <EventDetailsModal
            eventDetails={selectedEvent}
            onClose={handleCloseModal}
            triggerViewTokens={(id) => {
              handleCloseModal();
              handleShowTokenList(null, id);
            }}
            triggerDownloadReport={(filePath) =>
              handleExportReport(selectedEvent.event_id, dispatch)
            }
          />
        )}
        <TokenDetailsModal
          eventDetails={tokenListDetails}
          closeModal={() => setTokenListDetails(null)}
        />
        <OperatorDetailsModal
          eventDetails={operatorListDetails}
          closeModal={() => setOperatorListDetails(null)}
        />
      </div>
    </div>
  );
};

export default CustomerUpcomingEvents;
